import { Fragment, memo } from "react";
import InfoHint from "../../../../components/infoHint/InfoHint";
import {
  PERMISSION_LABELS,
  PERMISSION_PLACES,
  sectionPermissions,
} from "../../../../constants/permissionCatalog";
import PermissionSwitch from "./PermissionSwitch";
import styles from "./rolesPermissions.module.css";

const LinkGlyph = () => (
  <svg className={styles.linkGlyph} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5" />
    <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5" />
  </svg>
);

// Other places the same permission controls, so flipping it here flips them too.
const LinkedHint = ({ permission, path }) => {
  const others = (PERMISSION_PLACES[permission] || []).filter((place) => place !== path);
  if (others.length === 0) return null;
  return (
    <span className={styles.linkedHint} title={`نفس الصلاحية تتحكم أيضاً في:\n${others.join("\n")}`}>
      <LinkGlyph />
      {others.length}
    </span>
  );
};

const MissingHint = ({ missing }) =>
  missing.length === 0 ? null : (
    <span className={styles.missingHint}>
      الخادم يرفضها بدون: {missing.map((key) => PERMISSION_LABELS[key] || key).join("، ")}
    </span>
  );

const Node = ({ label, permission, path, held, enabled, readOnly, alsoNeeds, hint, depth = 0, onToggle }) => {
  const small = depth > 0;
  const isOn = held.has(permission);
  const visible = enabled && isOn;
  const missing = visible ? (alsoNeeds || []).filter((key) => !held.has(key)) : [];

  return (
    <div
      className={`${styles.nodeRow} ${small ? styles.nodeRowAction : ""} ${enabled ? "" : styles.nodeRowDisabled}`}
      style={depth > 1 ? { paddingInlineStart: depth * 26 } : undefined}
    >
      <div className={styles.nodeMain}>
        <PermissionSwitch
          small={small}
          checked={isOn}
          disabled={readOnly || !enabled}
          onToggle={() => onToggle(permission, !isOn)}
          label={label}
        />
        <span className={styles.nodeLabel}>{label}</span>
        {hint && <InfoHint text={hint} label={`توضيح: ${label}`} />}
        <LinkedHint permission={permission} path={path} />
      </div>
      <MissingHint missing={missing} />
    </div>
  );
};

// A button's own buttons (e.g. the tables page under the tournament card's
// tables button), indented one level deeper each time.
const ActionTree = ({ actions, depth, parentPath, enabled, held, readOnly, onToggle }) =>
  (actions || []).map((action) => {
    const path = `${parentPath} › ${action.label}`;
    const actionOn = enabled && held.has(action.permission);
    return (
      <Fragment key={action.id}>
        <Node
          depth={depth}
          label={action.label}
          permission={action.permission}
          path={path}
          held={held}
          enabled={enabled}
          readOnly={readOnly}
          alsoNeeds={action.alsoNeeds}
          hint={action.hint}
          onToggle={onToggle}
        />
        <ActionTree
          actions={action.actions}
          depth={depth + 1}
          parentPath={path}
          enabled={actionOn}
          held={held}
          readOnly={readOnly}
          onToggle={onToggle}
        />
      </Fragment>
    );
  });

const permissionsIn = (actions) =>
  (actions || []).flatMap((action) => [action.permission, ...permissionsIn(action.actions)]);

const SectionCard = ({ section, held, readOnly, accentFrom, accentTo, onToggle }) => {
  // A section has no permission of its own: it's on while any tab is, and its
  // header switch turns every tab on or off.
  const tabPermissions = sectionPermissions(section);
  const sectionOn = tabPermissions.some((key) => held.has(key));
  const toggle = (permission, nextOn) => onToggle(permission, nextOn, section.id);
  const toggleSection = () => {
    for (const key of tabPermissions) {
      if (held.has(key) === sectionOn) toggle(key, !sectionOn);
    }
  };

  const toggleable = [
    ...new Set(section.pages.flatMap((page) => [page.permission, ...permissionsIn(page.actions)])),
  ];
  const onCount = toggleable.filter((key) => held.has(key)).length;

  return (
    <section
      className={`${styles.sectionCard} ${sectionOn ? styles.sectionCardOn : ""}`}
      style={{ "--accent-from": accentFrom, "--accent-to": accentTo }}
    >
      <header className={styles.sectionHeader}>
        <span className={styles.sectionIconTile}>
          <section.icon className={styles.sectionIcon} />
        </span>
        <div className={styles.sectionTitleBlock}>
          <span className={styles.sectionTitle}>{section.label}</span>
          <span className={styles.sectionMeta}>{`${onCount} / ${toggleable.length} مفعّلة`}</span>
        </div>
        <PermissionSwitch
          checked={sectionOn}
          disabled={readOnly}
          onToggle={toggleSection}
          label={section.label}
        />
      </header>

      <div className={styles.sectionBody}>
        {section.pages.map((page) => {
          const pagePath = `${section.label} › ${page.label}`;
          const pageOn = held.has(page.permission);
          return (
            <div key={page.id} className={styles.pageBlock}>
              <Node
                label={page.label}
                permission={page.permission}
                path={pagePath}
                held={held}
                enabled
                readOnly={readOnly}
                alsoNeeds={page.alsoNeeds}
                hint={page.hint}
                onToggle={toggle}
              />
              <ActionTree
                actions={page.actions}
                depth={1}
                parentPath={pagePath}
                enabled={pageOn}
                held={held}
                readOnly={readOnly}
                onToggle={toggle}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default memo(SectionCard);
